import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '../stores/gameStore';
import { useSocket } from '../hooks/useSocket';

const SCAN_MESSAGES = [
  'ANALYZING OBSCURE INTERESTS...',
  'SCANNING FOR MAIN CHARACTER ENERGY...',
  'REJECTING PRETENTIOUS TOPICS...',
  'PURGING UNFAIR ADVANTAGES...',
  'SIMPLIFYING THE VIBE...'
];

const NicheAnalysisView = () => {
  const { gameState } = useGameStore();
  const { leaveRoom } = useSocket();
  const [scanningIdx, setScanningIdx] = useState(0);
  const [scanStatus, setScanStatus] = useState('DETECTING GATEKEEPERS...');

  const nicheNames = gameState.nicheCategory?.split(', ').map(s => s.trim()) || [];
  const isRevealed = nicheNames.length > 0;

  useEffect(() => {
    if (!isRevealed) {
      const msgInterval = setInterval(() => {
        setScanStatus(SCAN_MESSAGES[Math.floor(Math.random() * SCAN_MESSAGES.length)]);
      }, 1500);

      const scanInterval = setInterval(() => {
        setScanningIdx(prev => (prev + 1) % gameState.categories.length);
      }, 150);

      return () => {
        clearInterval(msgInterval);
        clearInterval(scanInterval);
      };
    }
  }, [isRevealed, gameState.categories.length]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="w-full max-w-2xl px-4 flex flex-col items-center space-y-8"
    >
      {/* Leave Room Button */}
      <button
        onClick={leaveRoom}
        className="self-start text-[10px] font-black uppercase tracking-widest text-white/30 hover:text-red-500 transition-colors"
      >
        LEAVE ROOM
      </button>

      {/* Target Scanner */}
      <div className="relative flex justify-center mb-4">
        <motion.div
          animate={
            isRevealed
              ? { scale: [1, 1.05, 1], borderColor: '#ef4444' }
              : { scale: [1, 1.2, 1], rotate: [0, 180, 360] }
          }
          transition={{ duration: 4, repeat: Infinity }}
          className="w-40 h-40 border-b-8 border-neon-purple rounded-full flex items-center justify-center relative transition-colors duration-500"
        >
          <span className="text-6xl">{isRevealed ? '🚫' : '🧐'}</span>
        </motion.div>
        <motion.div
          animate={{ opacity: [0, 1, 0] }}
          transition={{ duration: 1, repeat: Infinity }}
          className={`absolute inset-0 blur-3xl rounded-full ${
            isRevealed ? 'bg-red-500/20' : 'bg-neon-purple/20'
          }`}
        />
      </div>

      {/* Status */}
      <div className="text-center space-y-4">
        <h2 className="text-4xl font-black italic tracking-tighter text-neon-blue uppercase">
          {isRevealed ? 'ANALYSIS COMPLETE' : scanStatus}
        </h2>

        <AnimatePresence mode="wait">
          {isRevealed && gameState.nicheCommentary && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-600/10 border-2 border-red-500/40 p-6 rounded-[2rem] shadow-2xl"
            >
              <p className="text-[10px] font-black uppercase text-red-500 tracking-[0.3em] mb-2">
                Bartender's Verdict
              </p>
              <p className="text-2xl font-black italic text-white leading-tight">
                "{gameState.nicheCommentary}"
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Grid of Categories */}
      <div className="w-full grid grid-cols-2 sm:grid-cols-3 gap-3">
        {gameState.categories.map((c, idx) => {
          const isTarget = nicheNames.includes(c.text);
          const isScanning = !isRevealed && idx === scanningIdx;

          return (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{
                opacity: 1,
                scale: isTarget ? 1.05 : 1,
                x: isTarget ? [0, -2, 2, -2, 2, 0] : 0
              }}
              transition={isTarget ? { repeat: Infinity, duration: 0.2 } : {}}
              className={`relative glass p-4 rounded-2xl flex items-center justify-center text-center transition-all border-b-4 ${
                isTarget
                  ? 'border-red-600 bg-red-950/40 shadow-[0_0_20px_rgba(220,38,38,0.4)]'
                  : isScanning
                  ? 'border-neon-blue bg-neon-blue/20'
                  : 'border-white/10 opacity-40'
              }`}
            >
              <span
                className={`text-xs font-black uppercase tracking-tight ${
                  isTarget ? 'text-red-500 line-through' : 'text-white'
                }`}
              >
                {c.text}
              </span>

              {isTarget && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <div className="bg-red-600 text-white font-black text-[10px] px-2 py-0.5 rounded rotate-[-15deg] shadow-lg">
                    PURGED
                  </div>
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </div>

      <div className="pt-4 text-center">
        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-white/30">
          {isRevealed ? 'Transitioning to floor voting...' : 'Scanning the database for elitism...'}
        </p>
      </div>
    </motion.div>
  );
};

export default NicheAnalysisView;
